import type { ErrorLoggedEvent, SubscriptionCanceledEvent } from '../../../types';
import type { Theme as MuiTheme } from '@mui/material/styles';
import type { Step as StepType } from '../../../types/cancellation';

import * as styles from './index.css';
import {
  EventScopeProvider,
  TrackedButton,
  TrackedLink,
  useAnalytics,
  useViewTracking,
} from '@blueshift-ui/analytics';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Step, StepButton, Stepper } from '@blueshift-ui/navigation/dist/components/stepper';
import { StructuredText, StructuredTextGraphQlResponse } from 'react-datocms/structured-text';
import ACCOUNT_ROUTES from '../../../constants/account-routes';
import ACTION_KEYS from '../../../constants/action-keys';
import ActionPanel from '../../../components/membership/update/panels/action';
import AsyncActionButton from '@blueshift-ui/inputs/dist/components/async-action-button';
import CircularProgress from '@blueshift-ui/core/dist/components/circular-progress';
import ConfirmCancellationStep from '../../../components/membership/update/views/confirm-cancellation-step';
import EVENT_NAMES from '../../../constants/generated/event-names';
import EntitlementComparisonTable from '../../../components/membership/update/views/entitlement-comparison-table';
import SUB_STATUSES from '../../../constants/sub-statuses';
import Stack from '@blueshift-ui/core/dist/components/stack';
import getDowngradeOptions from '../../../helpers/data/get-downgrade-options';
import logHandledError from '@blueshift-ui/telemetry/dist/helpers/log-handled-error';
import useAuthenticatedUser from '@blueshift-ui/auth/dist/hooks/use-authenticated-user';
import useCancelSubscription from '../../../hooks/cancellation/use-cancel-subscription';
import useCancellationFlow from '../../../hooks/cancellation/use-cancellation-flow';
import useFeatureFlags from '@blueshift-ui/auth/dist/hooks/use-feature-flags';
import useMediaQuery from '@blueshift-ui/theme/dist/hooks/use-media-query';
import useSubscription from '../../../hooks/subscription/use-subscription';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

function MembershipCancelPage() {
  const analytics = useAnalytics();
  const { translate } = useTranslation('cancel_membership_page');
  const { subscriptionId } = useParams() as { subscriptionId: string };
  const { isLoading: isLoadingUser, data: user } = useAuthenticatedUser();
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription(subscriptionId, {
    params: { include: 'upgrade_downgrade_options', jurisdiction_id: user?.jurisdiction_id },
  });

  useViewTracking({
    type: 'content',
    trackedProperties: {
      content_name: 'cancel_membership_page',
    },
  });

  const navigate = useNavigate();
  const { state } = useLocation();
  const isLargeViewport = useMediaQuery((theme: MuiTheme) => theme.breakpoints.up('lg'));

  const { makeRequestCancelSubscription } = useCancelSubscription({
    subscriptionId,
  });

  const { data: cancellationFlow, isLoading: isCancellationFlowLoading } = useCancellationFlow({
    cancellationReason: state?.cancellationReason,
    userId: user?.user_id,
  });
  const { flags } = useFeatureFlags();

  const isCancellationEnabled = flags['kzn-879-self-service-cancellation'];
  const keepCurrentPlanKey = `keep_current_plan_action${isLargeViewport ? '' : '_mobile'}`;

  function getPageSection(step: number) {
    return `cancel_membership_step_${getStepAction(step)}`;
  }

  function getStepAction(step: number) {
    switch (step) {
      case 1:
        return 'action';
      case 2:
        return 'save';
      default:
        return 'cancel';
    }
  }

  function handleNextStep(setActiveStep: (step: number | ((prevStep: number) => number)) => void) {
    setActiveStep((prevStep) => {
      return prevStep + 1;
    });
  }

  async function handleSubmitCancel() {
    try {
      await makeRequestCancelSubscription({
        cancelImmediately: false,
        cancelState: 'end_of_billing_period',
        reasons: [state?.cancellationReason],
        sendEmailConfirmation: true,
      });

      analytics.track(EVENT_NAMES.subscriptionCanceled, {
        subscription_id: subscriptionId,
        page_path: window.location.pathname,
        page_section: getPageSection(3),
      } satisfies SubscriptionCanceledEvent);

      navigate(`/${ACCOUNT_ROUTES.membership}`, { state: { subscriptionId } });
    } catch (err: any) {
      analytics.track(EVENT_NAMES.errorLogged, {
        error_category: 'api_failure',
        error_message: err instanceof Error ? err.message : String(err),
        error_severity: 'critical',
        metadata: {
          subscription_id: subscriptionId,
        },
        page_path: window.location.pathname,
        page_section: getPageSection(3),
        user_impact: 'blocking',
      } satisfies ErrorLoggedEvent);

      logHandledError(err.message, {
        cancelImmediately: false,
        cancelState: 'end_of_billing_period',
        reasons: [state?.cancellationReason],
        subscriptionId,
      });
    }
  }

  // Determines if the step should be shown based on the subscription and the step's action panel
  function showStep(step: StepType) {
    const isFreeMonthCard = step.actionPanel.actionCards.some(
      ({ actionButton }) =>
        actionButton.actionKey === ACTION_KEYS.FREE_MONTH_AND_REQUEST_TUTOR ||
        actionButton.actionKey === ACTION_KEYS.REDEEM_FREE_MONTH
    );

    const isDowngradeCard = step.actionPanel.hasDowngradeOptions;

    if (isFreeMonthCard) {
      const hasClaimedFreeMonth =
        subscription?.sub_statuses?.includes(SUB_STATUSES.FREE_MONTH_CONCESSION_PENDING) ||
        !subscription?.free_month_claim_possible;
      return !hasClaimedFreeMonth;
    } else if (isDowngradeCard) {
      const hasDowngradeOptionsAvailable =
        subscription && getDowngradeOptions(subscription)?.length;
      return hasDowngradeOptionsAvailable;
    }

    return true;
  }

  if (isLoadingUser || isCancellationFlowLoading || subscriptionLoading) {
    return (
      <Stack>
        <CircularProgress />
      </Stack>
    );
  }

  if (!isCancellationEnabled || !cancellationFlow || !subscription) {
    return null;
  }

  return (
    <main>
      <Stepper>
        {cancellationFlow.steps.map((step, index) => {
          return showStep(step) ? (
            <Step className={styles.container} key={index}>
              <EventScopeProvider eventScope={{ pageSection: getPageSection(index + 1) }}>
                <div className={styles.content}>
                  <TrackedLink
                    component={RouterLink}
                    id="back_to_account_link"
                    to={`/${ACCOUNT_ROUTES.membership}`}
                  >
                    {translate('back_to_account')}
                  </TrackedLink>

                  <ActionPanel {...step.actionPanel} subscription={subscription} />

                  <div className={styles.actions}>
                    <TrackedButton
                      color="secondary"
                      component={RouterLink}
                      id="keep_current_plan_button"
                      to={`/${ACCOUNT_ROUTES.membership}?unchangedSubscription=true`}
                    >
                      {translate(keepCurrentPlanKey)}
                    </TrackedButton>

                    <StepButton
                      component={TrackedButton}
                      id="cancel_paid_plan_button"
                      onClick={handleNextStep}
                      variant="outlined"
                    >
                      {translate('cancel_paid_plan_action')}
                    </StepButton>
                  </div>
                </div>

                <div className={styles.benefits}>
                  <StructuredText
                    data={step.benefitPanel.content as StructuredTextGraphQlResponse}
                    renderBlock={({ record }: { record: any }) => {
                      switch (record.__typename) {
                        case 'SingleImageBlockRecord':
                          return (
                            <div className={styles.benefitImageContainer}>
                              <img alt={record?.image?.alt} src={record?.image?.url} />
                            </div>
                          );
                        default:
                          return null;
                      }
                    }}
                  />
                </div>
              </EventScopeProvider>
            </Step>
          ) : null;
        })}

        <Step className={styles.confirmationContainer}>
          <EventScopeProvider eventScope={{ pageSection: getPageSection(3) }}>
            <div className={styles.content}>
              <TrackedLink
                component={RouterLink}
                id="back_to_account_link"
                to={`/${ACCOUNT_ROUTES.membership}`}
              >
                {translate('back_to_account')}
              </TrackedLink>

              <ConfirmCancellationStep nextBillingDate={subscription.next_billing_date} />

              {!isLargeViewport ? null : (
                <section className={styles.actions}>
                  <TrackedButton
                    color="secondary"
                    component={RouterLink}
                    id="keep_current_plan_button"
                    to={`/${ACCOUNT_ROUTES.membership}?unchangedSubscription=true`}
                  >
                    {translate(keepCurrentPlanKey)}
                  </TrackedButton>

                  <AsyncActionButton
                    action={handleSubmitCancel}
                    className={styles.cancelAction}
                    id="cancel_paid_plan_button"
                  >
                    {translate('cancel_paid_plan_action')}
                  </AsyncActionButton>
                </section>
              )}
            </div>

            <EntitlementComparisonTable subscription={subscription} />

            {isLargeViewport ? null : (
              <section className={styles.actions}>
                <TrackedButton
                  color="secondary"
                  component={RouterLink}
                  id="keep_current_plan_button"
                  to={`/${ACCOUNT_ROUTES.membership}`}
                >
                  {translate(keepCurrentPlanKey)}
                </TrackedButton>

                <AsyncActionButton
                  action={handleSubmitCancel}
                  className={styles.cancelAction}
                  id="cancel_paid_plan_button"
                >
                  {translate('cancel_paid_plan_action')}
                </AsyncActionButton>
              </section>
            )}
          </EventScopeProvider>
        </Step>
      </Stepper>
    </main>
  );
}

export default MembershipCancelPage;
